import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  listUserClinicInteractions,
  upsertUserClinicInteraction,
} from "@louise/clinics/clinics.api";

type SavedClinicsContextType = {
  savedIds: Set<string>;
  notedIds: Set<string>;
  isSaved: (clinicId: string) => boolean;
  hasNotes: (clinicId: string) => boolean;
  toggleSave: (clinicId: string) => void;
  /** Call when entering detail screen to sync API state into context */
  syncFromApi: (clinicId: string, isLiked: boolean) => void;
  syncNotedFromApi: (clinicId: string, hasNotes: boolean) => void;
};

const SavedClinicsContext = createContext<SavedClinicsContextType | null>(null);

export function SavedClinicsProvider({ children }: { children: ReactNode }) {
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [notedIds, setNotedIds] = useState<Set<string>>(new Set());
  const { client, tenantId, patientId } = useApiClient();

  // Hydrate saved/noted sets from the backend on mount so the FindCare "Saved"
  // filter and note badges reflect persisted state without first re-opening
  // each clinic. Union into local state to avoid clobbering optimistic toggles.
  const { data: hydration } = useQuery({
    queryKey: ["provider-interactions", "list", tenantId],
    queryFn: () => listUserClinicInteractions(client, tenantId),
    // tenantId falls back to the default tenant when unauthenticated, so it
    // never gates anything — /me/* needs an authenticated patient (#1129).
    enabled: !!tenantId && !!patientId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!hydration) return;
    setSavedIds((prev) => {
      const next = new Set(prev);
      for (const it of hydration) if (it.isLiked) next.add(it.clinicId);
      return next;
    });
    setNotedIds((prev) => {
      const next = new Set(prev);
      for (const it of hydration) if (it.notes) next.add(it.clinicId);
      return next;
    });
  }, [hydration]);

  const isSaved = useCallback(
    (clinicId: string) => savedIds.has(clinicId),
    [savedIds],
  );

  // Persist a save/unsave with a few retries before giving up. Transient
  // failures (e.g. a 401 racing a Stytch token refresh, or a brief network
  // blip) would otherwise roll the optimistic toggle straight back, which the
  // user sees as the heart flickering on then off. Retrying first matches the
  // resilience React Query already gives the rest of the app.
  const persistSave = useCallback(
    async (clinicId: string, willSave: boolean) => {
      if (!tenantId) return;
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await upsertUserClinicInteraction(client, tenantId, clinicId, {
            isLiked: willSave,
          });
          return;
        } catch (err) {
          if (attempt === maxAttempts) {
            console.warn(
              "[SavedClinics] API sync failed after retries:",
              err,
            );
            // Roll back the optimistic toggle only once retries are exhausted.
            setSavedIds((curr) => {
              const rollback = new Set(curr);
              if (willSave) {
                rollback.delete(clinicId);
              } else {
                rollback.add(clinicId);
              }
              return rollback;
            });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
        }
      }
    },
    [client, tenantId],
  );

  const toggleSave = useCallback(
    (clinicId: string) => {
      let willSave = false;
      setSavedIds((prev) => {
        const next = new Set(prev);
        willSave = !next.has(clinicId);
        if (willSave) {
          next.add(clinicId);
        } else {
          next.delete(clinicId);
        }
        return next;
      });
      void persistSave(clinicId, willSave);
    },
    [persistSave],
  );

  const syncFromApi = useCallback((clinicId: string, isLiked: boolean) => {
    setSavedIds((prev) => {
      const has = prev.has(clinicId);
      if (has === isLiked) return prev;
      const next = new Set(prev);
      if (isLiked) {
        next.add(clinicId);
      } else {
        next.delete(clinicId);
      }
      return next;
    });
  }, []);

  const hasNotes = useCallback(
    (clinicId: string) => notedIds.has(clinicId),
    [notedIds],
  );

  const syncNotedFromApi = useCallback((clinicId: string, noted: boolean) => {
    setNotedIds((prev) => {
      const has = prev.has(clinicId);
      if (has === noted) return prev;
      const next = new Set(prev);
      if (noted) {
        next.add(clinicId);
      } else {
        next.delete(clinicId);
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      savedIds,
      notedIds,
      isSaved,
      hasNotes,
      toggleSave,
      syncFromApi,
      syncNotedFromApi,
    }),
    [
      savedIds,
      notedIds,
      isSaved,
      hasNotes,
      toggleSave,
      syncFromApi,
      syncNotedFromApi,
    ],
  );

  return (
    <SavedClinicsContext.Provider value={value}>
      {children}
    </SavedClinicsContext.Provider>
  );
}

export function useSavedClinics() {
  const ctx = useContext(SavedClinicsContext);
  if (!ctx) {
    throw new Error("useSavedClinics must be used within SavedClinicsProvider");
  }
  return ctx;
}
